import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { DisplayLocation } from '@repo/types';

/**
 * Maps the current route to a DisplayLocation for in-widget chatbot prompts.
 * Extracts kitId from the pathname directly (rather than useParams) so this
 * hook works from components rendered outside the route tree, such as the
 * TinyAIChatWindow in ResponsiveLayout.
 * Returns null for pages that have no associated prompts.
 */
export const useTinyAIDisplayLocation = (): DisplayLocation | null => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const hasKitId =
      /^\/results\/[^/]+/.test(pathname) ||
      /^\/action-plan\/[^/]+/.test(pathname);
    if (!hasKitId) return null;

    // Order matters: more specific routes first
    if (pathname.match(/^\/results\/[^/]+\/insights\/[^/]+\/\d+/)) {
      return 'metric_chatbot';
    }
    if (pathname.match(/^\/results\/[^/]+\/insights\/all-insights\/?$/)) {
      return 'all_insights_chatbot';
    }
    if (pathname.match(/^\/action-plan\/[^/]+\/?$/)) {
      return 'action_plan_chatbot';
    }
    if (pathname.match(/^\/results\/[^/]+\/?$/)) {
      return 'results_chatbot';
    }

    return null;
  }, [pathname]);
};
